/* ============================================================================
   Hot Cup Factory — quote builder catalogue adapter
   ----------------------------------------------------------------------------
   Turns the `quote_family` metaobjects into the two globals the builder wants:

     window.HCF_QUOTE_DATA   = { products: [...], questions: [...] }
     window.HCF_QUOTE_IMAGES = { <productId>: "https://cdn.shopify.com/..." }

   WHY THIS FILE EXISTS AT ALL
   assets/hcf-quote-builder.js is cut from the prototype by a script and must
   stay regenerable: edit the prototype, rerun the splitter, rerun the cut. The
   moment Shopify-specific mapping is written into the middle of it, that stops
   being true and the next regeneration silently loses the work. So every piece
   of Shopify knowledge lives here instead, and the seam between the two is the
   documented shape in handoff/README.md.

   LOAD ORDER MATTERS. The builder reads both globals on its first line. Both
   scripts are deferred, so they run in document order — this one is printed
   first by the section and must stay that way.

   Its input is the JSON printed by snippets/hcf-quote-catalogue.liquid, which
   is the metaobjects' own shape, not the builder's. The gap between the two is
   this file's whole job:

     metaobject shape          builder shape
     ────────────────────────────────────────────────────────────────────
     slug                      id
     sizes[].label/per/image   sizes[] as [label, artW, artH, unitsPerCase]
     sizes[].image             a separate id-keyed HCF_QUOTE_IMAGES map
     tiers: []  (unset)        a ladder derived from the minimum and case size
     lid: { title }            "Also quote matching <thing>"
     (nothing)                 questions
   ========================================================================== */
(function () {
  "use strict";

  var ROOT = document.querySelector("[data-quote-builder]");
  if (!ROOT) return;

  /* --------------------------------------------------------------------------
     The four questions.

     They are copy, not catalogue: no metaobject holds them, and nothing about
     them varies by product. Carried verbatim from handoff/catalogue.json. If
     they ever need to be merchant-editable they become a metoabject like the
     families did, and this block becomes another read.
     ------------------------------------------------------------------------ */
  var QUESTIONS = [
    {
      id: "when",
      q: "When do you need them in hand?",
      req: true,
      s: "We'll tell you right here if that's tight.",
      o: ["2–3 weeks", "About a month", "2–3 months", "Just planning ahead"]
    },
    {
      id: "artwork",
      q: "What have you got for artwork?",
      req: false,
      s: "Any of these is fine. We redraw for print at no charge.",
      o: ["Print-ready file", "A logo image", "Just my sign or socials", "Nothing yet, need help"]
    },
    {
      id: "use",
      q: "What's it for?",
      req: false,
      s: "So the right person prices it.",
      o: ["Café or coffee shop", "Restaurant", "Event", "Franchise group", "Reselling"]
    },
    {
      id: "cadence",
      q: "One-time or recurring?",
      req: false,
      wide: true,
      s: "If it turns into a repeat order we can move you onto better repeat pricing. Nothing to sign now.",
      o: [
        ["One-time order", "a single run"],
        ["Recurring", "better repeat pricing"],
        ["Not sure", "this is my first order"]
      ]
    }
  ];

  /* --------------------------------------------------------------------------
     Silhouette dimensions.

     Lifted verbatim from handoff/catalogue.json, where they were drawn by eye
     against real cups. They are NOT derivable: an 8 oz single wall is 34x58 and
     an 8 oz double wall is 36x60, because a double wall really is fatter. So
     they are keyed by family and by ounces, which is how they were measured.

     This is the FALLBACK path. A family whose products carry photographs never
     reaches it. A size added in Admin that is not in this table falls back to
     the nearest drawn size in its own family, which is close enough for a line
     drawing nobody will see — and if it is ever wrong enough to notice, the fix
     is to add the photograph, not to guess better numbers here.
     ------------------------------------------------------------------------ */
  var ART = {
    "single-wall":    { 8: [34, 58], 10: [36, 66], 12: [38, 78], 16: [40, 94], 20: [42, 104], 24: [44, 112] },
    "double-wall":    { 8: [36, 60], 10: [38, 68], 12: [40, 80], 16: [42, 96], 20: [44, 106] },
    "clear-pet":      { 8: [34, 62], 12: [38, 76], 16: [40, 90], 20: [42, 100], 24: [44, 108], 32: [47, 120] },
    "cold-paper":     { 12: [38, 77], 16: [40, 92], 22: [43, 106], 24: [44, 110] },
    "coffee-sleeves": { 24: [48, 54] },
    "mason-jars":     { 16: [48, 98] }
  };
  /* A family this file has never heard of still has to draw something. */
  var ART_FALLBACK = { 8: [34, 58], 12: [38, 78], 16: [40, 94], 24: [44, 112], 32: [47, 120] };

  function ozOf(label) {
    /* "16 oz" -> 16. "10-24 oz" -> 24: a sleeve should be drawn at the biggest
       cup it fits, not the smallest. */
    var nums = String(label).match(/\d+/g);
    return nums && nums.length ? parseInt(nums[nums.length - 1], 10) : 16;
  }

  function artDims(slug, label) {
    var table = ART[slug] || ART_FALLBACK;
    var oz = ozOf(label);
    if (table[oz]) return table[oz];

    var keys = Object.keys(table).map(Number);
    var near = keys[0];
    keys.forEach(function (k) {
      if (Math.abs(k - oz) < Math.abs(near - oz)) near = k;
    });
    return table[near];
  }

  /* --------------------------------------------------------------------------
     The quantity ladder, when a family has none configured.

     Mason jars are why. They state a minimum of 1,000 but ship 147 to a case,
     and part cases do not exist, so the real floor is seven cases: 1,029.
     Quoting 1,000 would name a number nobody can actually order.

     Deriving it rather than typing it means the ladder follows the case count
     if that ever changes. It is arithmetic, not pricing — which is exactly why
     a derived tier carries no discount badge. `disc` stays empty and the
     builder draws nothing against it, which is correct.

     For every family whose minimum already divides evenly into its case size,
     this returns the minimum unchanged.
     ------------------------------------------------------------------------ */
  function caseFloor(minUnits, per) {
    if (!per || per <= 0) return minUnits;
    return Math.ceil(minUnits / per) * per;
  }

  /* "Also quote matching 90mm lids" is an instruction to a rep, phrased as one.
     Built from the lid product's own title so it can never name a lid we do not
     stock. If a generated line ever reads badly, a `lid_label` field on the
     family beats parsing a product title — but that is one more field for
     somebody to fill in, so it waits until it is actually wrong. */
  function lidLine(fam) {
    if (!fam.offersLids || !fam.lid || !fam.lid.title) return null;
    var t = String(fam.lid.title)
      .replace(/^(UNIQ|Custom Printed|Custom)\s+/i, "")
      .replace(/\s*[-–]\s*\d.*$/, "")        /* drop a trailing "- 100/Case" */
      .trim()
      .toLowerCase();
    return t ? "Also quote matching " + t : null;
  }

  /* --------------------------------------------------------------------------
     Photo overrides set in the theme editor.

     The catalogue's own imagery is each family's first size product's featured
     image, which is the right default and needs nobody to do anything. But a
     product shot chosen to sell a case of blank cups is not always the shot
     that sells CUSTOM PRINTING — the tile wants a cup with somebody's logo on
     it. So the section carries an optional image per family, and it wins.

     Keyed by family slug, same key as everything else, so an override and the
     product it overrides can never drift apart.
     ------------------------------------------------------------------------ */
  function readOverrides() {
    var el = ROOT.querySelector("#hcf-quote-image-overrides");
    if (!el) return {};
    try {
      return JSON.parse(el.textContent) || {};
    } catch (e) {
      /* An override that fails to parse must not take the catalogue with it —
         the builder is still perfectly usable on product imagery alone. */
      warn("image overrides JSON is invalid — falling back to product images", e);
      return {};
    }
  }

  function readCatalogue() {
    /* Scoped to the section root, not the document. The Liquid prints the blob
       inside [data-quote-builder] precisely so this lookup can be, and so a
       stray copy elsewhere on the page can never be picked up. */
    var el = ROOT.querySelector("#hcf-quote-catalogue");
    if (!el) {
      warn("no catalogue blob found — is hcf-quote-catalogue rendered inside the section?");
      return [];
    }
    try {
      return JSON.parse(el.textContent) || [];
    } catch (e) {
      /* Malformed JSON here is a Liquid bug. Say so, and render an empty
         catalogue rather than throwing on line one and taking the section down
         with a blank screen and no explanation. */
      warn("catalogue JSON is invalid — check snippets/hcf-quote-catalogue.liquid", e);
      return [];
    }
  }

  function warn(msg, err) {
    if (window.console && window.console.warn) {
      window.console.warn("[hcf-quote-builder] " + msg, err || "");
    }
  }

  /* ------------------------------------------------------------------------ */

  var families = readCatalogue();
  families.sort(function (a, b) { return (a.order || 99) - (b.order || 99); });

  var overrides = readOverrides();
  var products = [];
  var images = {};

  families.forEach(function (f) {
    var sizes = f.sizes || [];
    if (!sizes.length) {
      /* Liquid already skips these — a family whose size products are all
         drafts resolves to nothing on the storefront. This is the belt to that
         braces, because a tile leading to an empty configurator is worse than
         a family that is simply not offered yet. */
      return;
    }

    var id = f.slug;

    /* One photograph per family, keyed by id — the shape the builder's IMAGES
       map expects. A theme-editor override wins outright; otherwise the first
       size that has an image, which on the storefront is the smallest, and the
       one the tile is drawn at anyway. */
    if (overrides[id]) {
      images[id] = overrides[id];
    } else {
      for (var i = 0; i < sizes.length; i++) {
        if (sizes[i].image) { images[id] = sizes[i].image; break; }
      }
    }

    var per0 = sizes[0].per || 0;
    var floor = caseFloor(f.minUnits || 0, per0);

    var product = {
      id:      id,
      group:   f.group || "Other",
      name:    f.name,
      shape:   f.shape || "cup",
      cold:    f.cold ? 1 : 0,
      unit:    sizes[0].noun || "units",
      blurb:   f.blurb || "",
      moq:     floor,
      lead:    f.leadTime || "",
      leadMax: f.leadWeeks || 6,
      note:    f.specNote || "",
      sizes:   sizes.map(function (s) {
        var d = artDims(id, s.label);
        return [s.label, d[0], d[1], s.per || 0];
      }),
      tiers: (f.tiers && f.tiers.length)
        ? f.tiers.slice()
        : [floor, floor * 2, floor * 4, floor * 8],
      disc: f.discounts || {}
    };

    /* `lid` is omitted entirely when there is nothing to quote, per the
       contract — an empty string would render an empty checkbox row. */
    var lid = lidLine(f);
    if (lid) product.lid = lid;

    products.push(product);
  });

  if (!products.length) {
    warn("catalogue is empty — the builder will render no products. " +
         "Check that quote_family metaobjects exist and their size products are published.");
  }

  window.HCF_QUOTE_DATA = { products: products, questions: QUESTIONS };
  window.HCF_QUOTE_IMAGES = images;
})();
